import { useCallback, useState } from 'react';
import { UseFormGetValues, UseFormReset, UseFormWatch } from 'react-hook-form';
import { Container, Grid, Stack, Typography } from '@mui/material';

import Form from 'components/Form';
import { QuillEditor } from 'components/QuillEditor';
import useNotification from 'hooks/useNotification';
import { postUploadFile } from './etc';
import { TGender } from 'apis/account/type';
import adornment from 'components/Form/adornment';

import MuiTextField from 'components/Form/MuiTextField';
import { FormProps } from '../PatientList/api/type';

import SearchPatientId from './SearchPatientId';

import { IUpdatePatient } from './type';

const MAX_MEDICAL_NOTE_IMAGE_SIZE_MB = 5;

interface Props extends FormProps {
  reset: UseFormReset<IUpdatePatient>;
  watch: UseFormWatch<IUpdatePatient>;
  getValues: UseFormGetValues<IUpdatePatient>;
  setIsSearched: (value: boolean) => void;
}

const PatientInfo = (props: Props) => {
  const { register, reset, setValue, getValues, setIsSearched, watch } = props;
  const { onFail, onResultCode, onSuccess } = useNotification();
  const [medicalNoteEditorKey, setMedicalNoteEditorKey] = useState(0);

  const handleMedicalNoteImageUpload = useCallback(
    async (file: File): Promise<string | null> => {
      if (file.size > MAX_MEDICAL_NOTE_IMAGE_SIZE_MB * 1024 * 1024) {
        onFail(
          '파일업로드 실패하였습니다',
          `업로드 가능한 용량을 초과하였습니다.(최대 ${MAX_MEDICAL_NOTE_IMAGE_SIZE_MB}MB)`,
        );
        return null;
      }

      try {
        const {
          data: { rc, bucket_url },
        } = await postUploadFile({ file });

        if (rc !== 1) {
          onResultCode(rc);
          return null;
        }

        onSuccess('파일업로드 성공하였습니다.');
        return bucket_url;
      } catch (error) {
        onFail('파일업로드 실패하였습니다', error);
        return null;
      }
    },
    [onFail, onResultCode, onSuccess],
  );

  const handleMedicalNoteVideoError = useCallback(
    (message: string) => {
      onFail(message, null);
    },
    [onFail],
  );

  const handleMedicalNoteChange = useCallback(
    (html: string) => {
      setValue('patient.medicalNote', html, { shouldDirty: true });
    },
    [setValue],
  );

  const onChangePatientId = (e: any) => {
    const { value } = e.target;
    const prev = getValues('patient.patientId');
    if (value !== prev) setIsSearched(false);
    setValue('patient.patientId', value);
  };

  return (
    <Container
      maxWidth={false}
      disableGutters
      component="section"
      sx={{ background: '#fff', p: 2 }}
    >
      <Stack spacing={2}>
        <Typography
          sx={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}
        >
          <span style={{ fontWeight: '400', color: '#2D73DC' }}>
            환자 정보(수정 가능)
          </span>
          <span style={{ fontSize: '12px', color: 'gray' }}>
            (환자 정보 중 주진단과 부진단 코드는 수정 및 추가 원하실 경우
            스마트널스 ENR로 문의해주세요.)
          </span>
        </Typography>

        <Grid container gap={2}>
          <Grid item xs={2.875}>
            <Stack spacing={1}>
              <Form.Item
                required
                label={
                  '환자ID (해당 학교의 가상 환자만 등록번호 검색을 통해 수정이 가능합니다.)'
                }
              >
                <MuiTextField
                  {...register('patient.patientId')}
                  onChange={onChangePatientId}
                  InputProps={{
                    endAdornment: (
                      <SearchPatientId
                        reset={reset}
                        getValues={getValues!}
                        setIsSearched={setIsSearched}
                        onPatientLoaded={() =>
                          setMedicalNoteEditorKey(key => key + 1)
                        }
                      />
                    ),
                  }}
                />
              </Form.Item>
              <Form.Item required label={'환자명'}>
                <MuiTextField
                  required
                  size="small"
                  {...register('patient.name')}
                />
              </Form.Item>
              <Form.Item required label={'나이'}>
                <MuiTextField
                  required
                  size="small"
                  {...register('patient.age')}
                />
              </Form.Item>
              <Form.Item required label={'성별'}>
                <Form.MuiRadioGroup
                  i18nKey="GENDER"
                  values={[1, 2, 3]}
                  defaultValue={
                    getValues('patient.gender')
                      ? getValues('patient.gender')
                      : 1
                  }
                  onChange={(value: TGender) =>
                    setValue('patient.gender', value)
                  }
                />
              </Form.Item>
              <Form.Item required label={'키'}>
                <MuiTextField
                  InputProps={{ ...adornment(undefined, '') }}
                  {...register('patient.height')}
                />
              </Form.Item>
            </Stack>
          </Grid>

          <Grid item xs={2.875}>
            <Stack spacing={1}>
              <Form.Item required label={'체중'}>
                <MuiTextField
                  InputProps={{ ...adornment(undefined, '') }}
                  {...register('patient.weight')}
                />
              </Form.Item>
              <Form.Item required label={'혈액형'}>
                <MuiTextField {...register('patient.blood')} />
              </Form.Item>
              <Form.Item required label={'HOD'}>
                <MuiTextField type="number" {...register('patient.adminHod')} />
              </Form.Item>
              <Form.Item label={'POD'}>
                <MuiTextField
                  type="number"
                  required={false}
                  {...register('patient.adminPod')}
                />
              </Form.Item>
            </Stack>
          </Grid>

          <Grid item xs={2.875}>
            <Stack spacing={1}>
              <Form.Item required label={'병동명'}>
                <MuiTextField {...register('patient.ward')} />
              </Form.Item>
              <Form.Item label={'병실명'}>
                <MuiTextField required={false} {...register('patient.room')} />
              </Form.Item>
              <Form.Item label={'진료과'}>
                <MuiTextField
                  required={false}
                  {...register('patient.department')}
                />
              </Form.Item>
              <Form.Item label={'담당의사'}>
                <MuiTextField
                  required={false}
                  {...register('patient.mainDoctor')}
                />
              </Form.Item>
            </Stack>
          </Grid>

          <Grid item xs={2.875}>
            <Stack spacing={1}>
              <Form.Item required label={'주진단코드'}>
                <MuiTextField
                  {...register('patient.diseaseMain')}
                  InputProps={{ readOnly: true }}
                />
              </Form.Item>
              <Form.Item label={'부진단코드1'}>
                <MuiTextField
                  {...register('patient.diseaseSub1')}
                  InputProps={{ readOnly: true }}
                />
              </Form.Item>
              <Form.Item label={'부진단코드2'}>
                <MuiTextField
                  {...register('patient.diseaseSub2')}
                  InputProps={{ readOnly: true }}
                />
              </Form.Item>
              <Form.Item label={'부진단코드3'}>
                <MuiTextField
                  {...register('patient.diseaseSub3')}
                  InputProps={{ readOnly: true }}
                />
              </Form.Item>
              <Form.Item label={'부진단코드4'}>
                <MuiTextField
                  {...register('patient.diseaseSub4')}
                  InputProps={{ readOnly: true }}
                />
              </Form.Item>
              <Form.Item label={'부진단코드5'}>
                <MuiTextField
                  {...register('patient.diseaseSub5')}
                  InputProps={{ readOnly: true }}
                />
              </Form.Item>
            </Stack>
          </Grid>
        </Grid>

        <Form.Item required label={'학교코드'} sx={{ maxWidth: 600 }}>
          <MuiTextField
            {...register('patient.collegeName')}
            InputProps={{ readOnly: true }}
          />
        </Form.Item>

        <Stack>
          <Typography variant="h5">환자상황 및 처방내역</Typography>
          <Form.Item
            required
            fullWidth
            label={'환자상황 및 처방내역'}
            sx={{ mt: 1.5 }}
          >
            <QuillEditor
              key={medicalNoteEditorKey}
              defaultValue={getValues('patient.medicalNote') ?? ''}
              onChange={handleMedicalNoteChange}
              onImageUpload={handleMedicalNoteImageUpload}
              onVideoError={handleMedicalNoteVideoError}
            />
          </Form.Item>
        </Stack>
      </Stack>
    </Container>
  );
};

export default PatientInfo;
